import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { ReactComponent as AddIcon } from '../../assets/add-icon.svg';
import { FieldArray, useFormikContext } from 'formik';
import { EventTitleEnum, LanguageCodeEnum } from '../../common/enums/common.enum';
import { HIDE_CHILD_EVENTS } from '../../common/constants/common.constants';
import { FormValues } from '../../pages/KindergartenDetails/components/EventTab/CreateConfiguration/CreateConfiguration';
import FastItemField from '../FastItemField';
import OptionOrChildEventRowActions from './OptionOrChildEventRowAction';
import { OptionOrChildEventItem } from '../../common/interfaces/event.interface';
import { emptyTranslations } from '../../utilities/helper/language.helper';

interface OptionOrChildEventFieldArrayProps {
  eventName: EventTitleEnum;
  lang: LanguageCodeEnum;
  fieldName: string;
  items: OptionOrChildEventItem[];
  eventIcons?: string[];
  type: 'childEvents' | 'options';
}

const OptionOrChildEventFieldArray: React.FC<OptionOrChildEventFieldArrayProps> = ({
  eventName,
  lang,
  eventIcons,
  fieldName,
  type,
  items
}) => {
  const { validateForm, setFieldValue } = useFormikContext<FormValues>();

  if (type === 'childEvents' && HIDE_CHILD_EVENTS.includes(eventName)) return null;

  const reindexOptions = (opts: any[]) => {
    return opts.map((opt, idx) => ({ ...opt, value: idx + 1 }));
  };

  const handleRemove = (index: number, remove: (index: number) => void) => {
    if (lang === LanguageCodeEnum.SRB) return;
    if (type === 'options') {
      const updated = [...items];
      updated.splice(index, 1);
      const reindexed = reindexOptions(updated);
      setFieldValue(fieldName, reindexed, false);
    } else {
      remove(index);
    }
    validateForm({ name: fieldName });
  };

  const handleAdd = (push: (item: OptionOrChildEventItem) => void) => {
    if (type === 'options') {
      push({ title: emptyTranslations(), icon: '', value: items.length + 1 });
    } else {
      push({ title: emptyTranslations(), icon: '' });
    }
  };

  return (
    <FieldArray name={fieldName}>
      {({ replace, push, remove }) => (
        <>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            px={2}
            pt={1.5}
            borderTop={type === 'options' ? '1px solid #ddd' : 'none'}
          >
            <Typography variant="caption">{type === 'childEvents' ? 'Child events:' : 'Options:'}</Typography>
            {lang === LanguageCodeEnum.ENG ? (
              <IconButton onClick={() => handleAdd(push)}>
                <AddIcon />
              </IconButton>
            ) : (
              <Box sx={{ width: 40, height: 40 }} />
            )}
          </Box>

          <Box display="flex" flexDirection="column" px={2} py={1.5}>
            {items.map((opt: OptionOrChildEventItem, index: number) => {
              return (
                <Box key={index} display="flex" alignItems="center" gap={1.5}>
                  <Box sx={{ flex: 1 }}>
                    <FastItemField index={index} fieldName={fieldName} lang={lang} />
                  </Box>

                  <OptionOrChildEventRowActions
                    index={index}
                    lang={lang}
                    opt={opt}
                    fieldName={fieldName}
                    replace={(index, value) => {
                      replace(index, value);
                      validateForm({ name: fieldName });
                    }}
                    eventIcons={eventIcons}
                    onRemove={() => {
                      handleRemove(index, remove);
                    }}
                    showIconSelector={type === 'childEvents' ? eventName === EventTitleEnum.TOILET : true}
                  />
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </FieldArray>
  );
};
export default OptionOrChildEventFieldArray;
